import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { apiFetch } from '@/lib/api';

export interface ClientRow {
  id: string;
  title: string | null;
  first_name: string | null;
  last_name: string;
  client_type: 'kupac' | 'prodavac' | 'najmodavac' | 'najmoprimac' | 'interessent';
  status: 'otvoreno' | 'na_cekanju' | 'odbijeno' | 'potpisan_ugovor' | 'zatvoreno';
  interest_status: 'none' | 'wants_viewing' | 'viewing_scheduled' | 'offer_made' | 'closed';
  phone: string;
  phone_2: string | null;
  email: string | null;
  address: string | null;
  location: string | null;
  birth_date: string | null;
  citizenship: string | null;
  sv_number: string | null;
  /** ISO code (de/en/fr/it/sr/bg/ru/tr/ar/zh). When the client signs
   *  a document via magic-link, the auto-send email attaches DE plus
   *  this language's translation if available. NULL = DE only. */
  preferred_language: string | null;
  platform: string | null;
  platform_code: string | null;
  notes_1: string | null;
  notes_2: string | null;
  notes_3: string | null;
  next_action: string | null;
  next_action_type: string | null;
  next_action_due: number | null;
  last_contact_at: number | null;
  assigned_agent_id: string | null;
  /** Seller-rules batch — optional SECOND makler with full rights on a
   *  seller (max 2 maklers per seller). NULL for non-sellers and for
   *  sellers without a second makler. */
  secondary_agent_id: string | null;
  /** Activity-compliance deadline (armed at Vermittlungsauftrag signing;
   *  1 Notiz per rolling 10 days). NULL = no active obligation.
   *  timestamp_ms — may arrive as ISO STRING (documented pitfall):
   *  parse with `typeof x === 'number' ? x : new Date(x).getTime()`. */
  activity_due_at: number | string | null;
  created_by: string;
  ownership_locked: boolean;
  /** 30-day exclusivity window — when set in the future, only the
   *  aufnahme_lock_agent_id can edit the client + linked properties.
   *  Reset on each new termin (aufnahme/besichtigung/kaufanbot).
   *  Cleared on cancel/reject. */
  aufnahme_lock_until: number | null;
  aufnahme_lock_agent_id: string | null;
  /** Obsolete since batch BE (30-day window abolished) — history only. */
  aufnahme_lock_extended_at?: number | null;
  /** Seller closure (batch BE): who closed (users.id), when, and the
   *  mandatory reason. Set only while status === 'zatvoreno'. */
  closed_at?: number | string | null;
  closed_by?: string | null;
  closed_reason?: string | null;
  /** DSGVO opt-out — when true, AI voice agent (Vapi) skips this client. */
  do_not_call?: boolean;
  created_at: number;
  updated_at: number;
  archived_at: number | null;
  /** Aggregated lifecycle indicator derived from related appointments.
   *  green > yellow > off > red > none priority. */
  traffic_light?: 'off' | 'yellow' | 'green' | 'red' | 'none';
}

export interface ClientFilters {
  search?: string;
  clientType?: string;
  /** Verkäufer / Käufer segments — filtered server-side, because „Alle" now
   *  spans ~83k rows and narrowing that in the browser would page forever. */
  clientTypes?: string[];
  status?: string;
  interestStatus?: string;
  agentId?: string;
  includeArchived?: boolean;
  limit?: number;
  offset?: number;
  /** v2 — operator audience scope. Default hides the Abgeber-Liste pool;
   *  'imported' returns only that pool (admin-only); 'all' is the
   *  escape hatch. */
  audience?: 'normal' | 'imported' | 'pool' | 'all';
  /** Batch ZO #6 — for CC users, "Meine" means "rows I created". */
  createdByUserId?: string;
}

export const clientsKey = (filters: ClientFilters = {}) => ['clients', filters] as const;

export interface ClientStats {
  /** Working scope — the clients an operator actively deals with. */
  total: number;
  /** EVERY non-archived client, cold cohorts included. This is the number
   *  the KPI strip headlines since 2026-08-24; `total` became its sub-line. */
  total_all?: number;
  archived: number;
  by_client_type: Record<string, number>;
  /** Same breakdown over every cohort — pairs with `total_all`. */
  by_client_type_all?: Record<string, number>;
  by_status: Record<string, number>;
  /** Anrufliste (Abgeber cold pool, unworked) — global count for the chip. */
  imported?: number;
  /** Kundenpool (claimable, unclaimed) — global count for the chip. */
  pool?: number;
}

export function useClientsStats() {
  return useQuery({
    queryKey: ['clients', 'stats'] as const,
    queryFn: async () => {
      const res = await apiFetch('/clients/stats');
      if (!res.ok) throw new Error(`clients_stats_failed_${res.status}`);
      return (await res.json()) as ClientStats;
    },
    staleTime: 30_000,
  });
}

// Per-maker count: how many clients are assigned to the calling profile
// under the normal scope vs the Anrufliste/Legacy bucket. Used by the
// dashboard hint AND the "Meine" segment chip on /clients to drive the
// count badge.
export function useMyClientCounts(enabled = true) {
  return useQuery({
    queryKey: ['clients', 'my-counts'] as const,
    queryFn: async () => {
      const res = await apiFetch('/clients/my-counts');
      if (!res.ok) throw new Error(`my_counts_failed_${res.status}`);
      return (await res.json()) as { visible: number; anrufliste: number };
    },
    enabled,
    staleTime: 60_000,
  });
}

export function useClients(filters: ClientFilters = {}) {
  return useQuery({
    queryKey: clientsKey(filters),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters.search) params.set('search', filters.search);
      if (filters.clientType) params.set('client_type', filters.clientType);
      else if (filters.clientTypes?.length) params.set('client_types', filters.clientTypes.join(','));
      if (filters.status) params.set('status', filters.status);
      if (filters.interestStatus) params.set('interest_status', filters.interestStatus);
      if (filters.agentId) params.set('agent_id', filters.agentId);
      if (filters.includeArchived) params.set('include_archived', 'true');
      if (filters.limit) params.set('limit', String(filters.limit));
      if (filters.offset) params.set('offset', String(filters.offset));
      if (filters.audience) params.set('audience', filters.audience);
      if (filters.createdByUserId)
        params.set('created_by_user_id', filters.createdByUserId);
      params.set('order_by', 'created_at');
      params.set('order', 'desc');
      const res = await apiFetch(`/clients?${params}`);
      if (!res.ok) throw new Error(`clients_list_failed_${res.status}`);
      const json = (await res.json()) as { items: ClientRow[] };
      return json.items;
    },
    staleTime: 30_000,
  });
}

export function useClient(id: string | undefined) {
  return useQuery({
    queryKey: ['client', id] as const,
    queryFn: async () => {
      const res = await apiFetch(`/clients/${id}`);
      if (!res.ok) throw new Error(`client_get_failed_${res.status}`);
      return (await res.json()) as ClientRow;
    },
    enabled: !!id,
  });
}

// Batch AH faza 2 — infinite scroll variant of useClients.
// `filters.limit` acts as page size (default 50). The query stops
// auto-fetching when the last page returned fewer rows than requested.
export function useInfiniteClients(
  filters: Omit<ClientFilters, 'offset'> = {},
) {
  const pageSize = filters.limit ?? 50;
  return useInfiniteQuery({
    queryKey: ['clients', 'infinite', { ...filters, limit: pageSize }] as const,
    queryFn: async ({ pageParam }) => {
      const params = new URLSearchParams();
      if (filters.search) params.set('search', filters.search);
      if (filters.clientType) params.set('client_type', filters.clientType);
      else if (filters.clientTypes?.length) params.set('client_types', filters.clientTypes.join(','));
      if (filters.status) params.set('status', filters.status);
      if (filters.interestStatus)
        params.set('interest_status', filters.interestStatus);
      if (filters.agentId) params.set('agent_id', filters.agentId);
      if (filters.includeArchived) params.set('include_archived', 'true');
      if (filters.audience) params.set('audience', filters.audience);
      if (filters.createdByUserId)
        params.set('created_by_user_id', filters.createdByUserId);
      params.set('limit', String(pageSize));
      params.set('offset', String(pageParam ?? 0));
      params.set('order_by', 'created_at');
      params.set('order', 'desc');
      const res = await apiFetch(`/clients?${params}`);
      if (!res.ok) throw new Error(`clients_list_failed_${res.status}`);
      const json = (await res.json()) as { items: ClientRow[] };
      return { items: json.items, offset: pageParam ?? 0 };
    },
    initialPageParam: 0 as number,
    getNextPageParam: (lastPage) =>
      lastPage.items.length < pageSize
        ? undefined
        : lastPage.offset + pageSize,
    staleTime: 30_000,
  });
}

export interface CreateClientPayload {
  title?: string | null;
  firstName?: string | null;
  lastName: string;
  clientType: ClientRow['client_type'];
  phone: string;
  phone2?: string | null;
  email?: string | null;
  address?: string | null;
  location?: string | null;
  birthDate?: string | null;
  citizenship?: string | null;
  svNumber?: string | null;
  preferredLanguage?:
    | 'de' | 'en' | 'fr' | 'it' | 'sr' | 'bg' | 'ru' | 'tr' | 'ar' | 'zh'
    | null;
  platform?: string | null;
  platformCode?: string | null;
  notes1?: string | null;
  notes2?: string | null;
  notes3?: string | null;
  interestStatus?: ClientRow['interest_status'];
  /** DSGVO opt-out — when true, KI-Sekretärin (Vapi) will skip this client. */
  doNotCall?: boolean;
}

export interface DuplicateMatch {
  id: string;
  title: string | null;
  first_name: string | null;
  last_name: string;
  client_type: ClientRow['client_type'];
  phone: string;
  email: string | null;
}

export interface DuplicateClientPayload {
  /** First (most relevant) match — kept for backwards compatibility. */
  existing: DuplicateMatch;
  /** All matches in the same dedup bucket (up to 3). Batch AK-1. */
  matches: DuplicateMatch[];
  bucket: 'seller' | 'buyer' | 'interessent';
  /** Batch AS — true when at least one of the matches is itself an
   *  Interessent. In that case override is always denied (regardless
   *  of admin role) because the right action is "convert the existing
   *  Interessent to Käufer", not "create a duplicate Käufer". */
  matchHasInteressent?: boolean;
  /** Batch AK-1 — seller / buyer buckets accept "Trotzdem anlegen"
   *  (re-POST with `forceCreate=true`); the interessent bucket does
   *  not. The frontend uses this to decide whether to render the
   *  override button. */
  allowOverride: boolean;
}

// Error subclass that preserves the duplicate payload so the dialog can
// offer "Open existing record" without a second round-trip.
export class DuplicateClientError extends Error {
  constructor(public readonly duplicate: DuplicateClientPayload) {
    super('duplicate_client');
    this.name = 'DuplicateClientError';
  }
}

/** Thrown when the Interessent intake gate (Batch AK-2) rejects a
 *  payload missing first_name or email. Carries the field list so the
 *  UI can highlight the offending inputs. */
export class InteressentMissingFieldsError extends Error {
  constructor(public readonly missing: string[]) {
    super('interessent_fields_missing');
    this.name = 'InteressentMissingFieldsError';
  }
}

/** Thrown when the Buyer intake gate (Batch AR) rejects a payload
 *  missing email. Buyers (kupac / najmoprimac) receive Kaufanbot PDFs,
 *  so email is operationally required per tester 2026-05-31. */
export class BuyerEmailRequiredError extends Error {
  constructor() {
    super('buyer_email_required');
    this.name = 'BuyerEmailRequiredError';
  }
}

export function useCreateClient() {
  const qc = useQueryClient();
  return useMutation({
    // Batch AK-1 — second argument carries the override flag without
    // bloating the CreateClientPayload type (which is also reused by
    // the bulk-import path that has no notion of "force").
    mutationFn: async (
      input:
        | CreateClientPayload
        | { payload: CreateClientPayload; forceCreate: boolean },
    ) => {
      const payload = 'payload' in input ? input.payload : input;
      const forceCreate = 'payload' in input ? input.forceCreate : false;
      const body = forceCreate ? { ...payload, forceCreate: true } : payload;
      const res = await apiFetch('/clients', {
        method: 'POST',
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = (await res.json().catch(() => null)) as
          | {
              error?: string;
              message?: string;
              cause?: DuplicateClientPayload | { missing?: string[] };
            }
          | null;
        if (res.status === 409 && err?.message === 'duplicate_client' && err.cause) {
          throw new DuplicateClientError(err.cause as DuplicateClientPayload);
        }
        if (
          res.status === 400 &&
          err?.message === 'interessent_fields_missing' &&
          err.cause &&
          'missing' in err.cause &&
          Array.isArray(err.cause.missing)
        ) {
          throw new InteressentMissingFieldsError(err.cause.missing);
        }
        if (res.status === 400 && err?.message === 'buyer_email_required') {
          throw new BuyerEmailRequiredError();
        }
        throw new Error(err?.error ?? err?.message ?? `client_create_failed_${res.status}`);
      }
      return (await res.json()) as ClientRow;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['clients'] }),
  });
}

export function useUpdateClient(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: Partial<CreateClientPayload> & { status?: ClientRow['status'] }) => {
      const res = await apiFetch(`/clients/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = (await res.json().catch(() => null)) as { error?: string } | null;
        throw new Error(err?.error ?? `client_update_failed_${res.status}`);
      }
      return (await res.json()) as ClientRow;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['clients'] });
      qc.invalidateQueries({ queryKey: ['client', id] });
    },
  });
}

/**
 * Marks a client's "next action" follow-up as done by clearing next_action,
 * next_action_type and next_action_due — so it drops off the dashboard's
 * "Anstehende Aktionen" list. Those columns are set by workflow events (offer
 * sent, viewing booked, interest change) and by Zoki reminders; before this
 * existed there was no way for a makler to complete one, so overdue reminders
 * piled up forever. Timeline notes / history stay intact — only the reminder is
 * cleared, and updateClient audits the change. Takes the id at call-time so one
 * hook instance can complete any row in a list.
 */
export function useCompleteClientAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await apiFetch(`/clients/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          nextAction: null,
          nextActionType: null,
          nextActionDue: null,
        }),
      });
      if (!res.ok) {
        const err = (await res.json().catch(() => null)) as { error?: string } | null;
        throw new Error(err?.error ?? `client_update_failed_${res.status}`);
      }
      return (await res.json()) as ClientRow;
    },
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: ['clients'] });
      qc.invalidateQueries({ queryKey: ['client', id] });
    },
  });
}

/**
 * Sets a private follow-up reminder (next_action) on a client — the makler's
 * own "call this person on date X" note that appears under "Anstehende
 * Aktionen" on the dashboard. This is deliberately NOT an appointment: it does
 * NOT send any SMS or e-mail to the client. Until this existed, only Zoki or
 * the automatic workflow could set such a reminder; a makler could not do it
 * herself (support: Susanne Kirsch, 01.10.2026). Cleared again via
 * useCompleteClientAction (the "Erledigt" button).
 */
export function useSetClientReminder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { id: string; action: string; dueMs: number }) => {
      const res = await apiFetch(`/clients/${vars.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          nextAction: vars.action,
          nextActionType: 'call',
          nextActionDue: vars.dueMs,
        }),
      });
      if (!res.ok) {
        const err = (await res.json().catch(() => null)) as { error?: string } | null;
        throw new Error(err?.error ?? `client_update_failed_${res.status}`);
      }
      return (await res.json()) as ClientRow;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['clients'] });
      qc.invalidateQueries({ queryKey: ['client', vars.id] });
    },
  });
}

// Kundenpool claim/delegate. Omit assignToProfileId to claim for self,
// or pass a target profile id to delegate. Backend graduates the client
// into the normal pool and arms the 30-day lock for the new owner.
export function useClaimPoolClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      assignToProfileId,
    }: {
      id: string;
      assignToProfileId?: string;
    }) => {
      const res = await apiFetch(`/clients/${id}/claim`, {
        method: 'POST',
        body: JSON.stringify(
          assignToProfileId ? { assign_to_profile_id: assignToProfileId } : {},
        ),
      });
      if (!res.ok) {
        const err = (await res.json().catch(() => null)) as { message?: string } | null;
        throw new Error(err?.message ?? `pool_claim_failed_${res.status}`);
      }
      return (await res.json()) as ClientRow;
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ['clients'] });
      // Invalidate the single-client cache so an open detail dialog
      // (useClient) refetches and the claim banner flips to the lock banner.
      qc.invalidateQueries({ queryKey: ['client', variables.id] });
    },
  });
}

// Close a seller ("Ugasi prodavca", batch BE) — mandatory reason; records
// WHO/WHEN/WHY (closed_at/closed_by/closed_reason + history). Any role.
export function useCloseClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason: string }) => {
      const res = await apiFetch(`/clients/${id}/close`, {
        method: 'POST',
        body: JSON.stringify({ reason }),
      });
      if (!res.ok) {
        const err = (await res.json().catch(() => null)) as { message?: string } | null;
        throw new Error(err?.message ?? `client_close_failed_${res.status}`);
      }
      return (await res.json()) as ClientRow;
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ['clients'] });
      qc.invalidateQueries({ queryKey: ['client', variables.id] });
      qc.invalidateQueries({ queryKey: ['client', variables.id, 'history'] });
    },
  });
}

// Reopen a closed seller — admin only (closure was a mistake).
export function useReopenClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await apiFetch(`/clients/${id}/reopen`, { method: 'POST' });
      if (!res.ok) {
        const err = (await res.json().catch(() => null)) as { message?: string } | null;
        throw new Error(err?.message ?? `client_reopen_failed_${res.status}`);
      }
      return (await res.json()) as ClientRow;
    },
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: ['clients'] });
      qc.invalidateQueries({ queryKey: ['client', id] });
      qc.invalidateQueries({ queryKey: ['client', id, 'history'] });
    },
  });
}

// ── Second makler (seller-rules batch) ─────────────────────────────────
// Sellers may carry a SECOND makler with full rights
// (clients.secondary_agent_id, max 2 maklers per seller). Backend gate:
// any admin on unsigned sellers; signed (AV) sellers only compliance
// managers (403 compliance_manager_only). Target must be an active
// makler ≠ primary (400 invalid_assignee / 400 already_primary).
export function useAssignSecondaryAgent(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (agentProfileId: string) => {
      const res = await apiFetch(`/clients/${id}/secondary-agent`, {
        method: 'POST',
        body: JSON.stringify({ agent_profile_id: agentProfileId }),
      });
      if (!res.ok) {
        const err = (await res.json().catch(() => null)) as
          | { message?: string; error?: string }
          | null;
        throw new Error(
          err?.message ?? err?.error ?? `secondary_assign_failed_${res.status}`,
        );
      }
      return (await res.json()) as ClientRow;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['clients'] });
      qc.invalidateQueries({ queryKey: ['client', id] });
      qc.invalidateQueries({ queryKey: ['client', id, 'history'] });
    },
  });
}

export function useRemoveSecondaryAgent(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const res = await apiFetch(`/clients/${id}/secondary-agent`, {
        method: 'DELETE',
      });
      if (!res.ok) {
        const err = (await res.json().catch(() => null)) as
          | { message?: string; error?: string }
          | null;
        throw new Error(
          err?.message ?? err?.error ?? `secondary_remove_failed_${res.status}`,
        );
      }
      return (await res.json()) as ClientRow;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['clients'] });
      qc.invalidateQueries({ queryKey: ['client', id] });
      qc.invalidateQueries({ queryKey: ['client', id, 'history'] });
    },
  });
}

// Bulk Kundenpool claim/delegate — claim many pool rows at once. Backend
// skips rows that aren't unclaimed pool clients.
export function useBulkClaimPool() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      ids,
      assignToProfileId,
    }: {
      ids: string[];
      assignToProfileId?: string;
    }) => {
      const res = await apiFetch('/clients/bulk', {
        method: 'POST',
        body: JSON.stringify({
          ids,
          op: 'claim',
          payload: assignToProfileId ? { assignToProfileId } : {},
        }),
      });
      if (!res.ok) throw new Error(`bulk_claim_failed_${res.status}`);
      return (await res.json()) as {
        ok: boolean;
        success: number;
        failed: number;
        errors: string[];
      };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['clients'] });
      // Bulk touches many ids — invalidate all single-client caches too.
      qc.invalidateQueries({ queryKey: ['client'] });
    },
  });
}

// Backend converts DELETE to a soft-archive (archived_at = now). Hook name
// kept for call-site compatibility.
export function useDeleteClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await apiFetch(`/clients/${id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error(`client_archive_failed_${res.status}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['clients'] }),
  });
}

export function useRestoreClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await apiFetch(`/clients/${id}/restore`, { method: 'POST' });
      if (!res.ok) throw new Error(`client_restore_failed_${res.status}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['clients'] }),
  });
}

export interface ClientHistoryRow {
  id: string;
  client_id: string;
  action: string;
  field_changed: string | null;
  old_value: string | null;
  new_value: string | null;
  changed_by: string | null;
  automatic_change: number;
  change_reason: string | null;
  created_at: number;
}

export function useClientHistory(id: string | undefined) {
  return useQuery({
    queryKey: ['client', id, 'history'] as const,
    queryFn: async () => {
      const res = await apiFetch(`/clients/${id}/history`);
      if (!res.ok) throw new Error(`client_history_failed_${res.status}`);
      const json = (await res.json()) as { items: ClientHistoryRow[] };
      return json.items;
    },
    enabled: !!id,
  });
}

// ── Historische Notizen (timeline notes, Phase 14.8b) ─────────────────

export interface TimelineNoteRow {
  id: string;
  client_id: string;
  note_date: number;
  body: string;
  created_by: string;
  created_at: number;
  archived_at: number | null;
}

export function useClientTimelineNotes(id: string | undefined) {
  return useQuery({
    queryKey: ['client', id, 'notes'] as const,
    queryFn: async () => {
      const res = await apiFetch(`/clients/${id}/notes`);
      if (!res.ok) throw new Error(`client_notes_failed_${res.status}`);
      const json = (await res.json()) as { items: TimelineNoteRow[] };
      return json.items;
    },
    enabled: !!id,
  });
}

export function useCreateTimelineNote(clientId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { noteDate: number; body: string }) => {
      const res = await apiFetch(`/clients/${clientId}/notes`, {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`note_create_failed_${res.status}`);
      return (await res.json()) as TimelineNoteRow;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['client', clientId, 'notes'] }),
  });
}

export function useDeleteTimelineNote(clientId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (noteId: string) => {
      const res = await apiFetch(`/clients/${clientId}/notes/${noteId}`, {
        method: 'DELETE',
      });
      if (!res.ok) throw new Error(`note_delete_failed_${res.status}`);
      return res.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['client', clientId, 'notes'] }),
  });
}

export interface ClientPropertyRow {
  id: string;
  client_id: string;
  address: string | null;
  district: string | null;
  postal_code: string | null;
  rooms_count: number | null;
  living_area: number | null;
  purchase_price: number | null;
  status: string | null;
  building_type: string | null;
  object_type: string | null;
  edi_number: string | null;
  created_at: number;
}

export function useClientProperties(id: string | undefined) {
  return useQuery({
    queryKey: ['client', id, 'properties'] as const,
    queryFn: async () => {
      const res = await apiFetch(`/clients/${id}/properties`);
      if (!res.ok) throw new Error(`client_properties_failed_${res.status}`);
      const json = (await res.json()) as { items: ClientPropertyRow[] };
      return json.items;
    },
    enabled: !!id,
  });
}

export interface ClientAppointmentRow {
  id: string;
  client_id: string;
  property_id: string | null;
  agent_id: string | null;
  appointment_type: string;
  appointment_date: number;
  status: string;
  location: string | null;
  notes: string | null;
  created_at: number;
}

export function useClientAppointments(id: string | undefined) {
  return useQuery({
    queryKey: ['client', id, 'appointments'] as const,
    queryFn: async () => {
      const res = await apiFetch(`/clients/${id}/appointments`);
      if (!res.ok) throw new Error(`client_appointments_failed_${res.status}`);
      const json = (await res.json()) as { items: ClientAppointmentRow[] };
      return json.items;
    },
    enabled: !!id,
  });
}

export interface ContactAgentRow {
  profile_id: string;
  agent_id: string;
  full_name: string;
  role: string;
  avatar_key: string | null;
  total_appointments: number;
  last_contact_at: number | null;
}

export function useClientContactAgents(id: string | undefined) {
  return useQuery({
    queryKey: ['client', id, 'contact-agents'] as const,
    queryFn: async () => {
      const res = await apiFetch(`/clients/${id}/contact-agents`);
      if (!res.ok) throw new Error(`client_contact_agents_failed_${res.status}`);
      const json = (await res.json()) as { items: ContactAgentRow[] };
      return json.items;
    },
    enabled: !!id,
  });
}

export interface ClientExternalInterestRow {
  id: string;
  client_id: string;
  external_url: string;
  external_source: string;
  snapshot_json: string;
  notes: string | null;
  status: 'active' | 'inactive' | 'closed';
  created_by: string | null;
  created_at: number;
  archived_at: number | null;
}

export function useClientExternalInterests(id: string | undefined) {
  return useQuery({
    queryKey: ['client', id, 'external-interests'] as const,
    queryFn: async () => {
      const res = await apiFetch(`/clients/${id}/external-interests`);
      if (!res.ok) throw new Error(`client_external_interests_failed_${res.status}`);
      const json = (await res.json()) as { items: ClientExternalInterestRow[] };
      return json.items;
    },
    enabled: !!id,
  });
}

export interface ImportClientRow {
  title?: string | null;
  firstName?: string | null;
  lastName: string;
  clientType: 'kupac' | 'prodavac' | 'najmodavac' | 'najmoprimac' | 'interessent';
  phone: string;
  phone2?: string | null;
  email?: string | null;
  location?: string | null;
  address?: string | null;
  platform?: string | null;
  platformCode?: string | null;
  notes1?: string | null;
}

export interface ImportResult {
  total: number;
  created: number;
  updated: number;
  skipped: number;
  errors: Array<{ index: number; phone: string | null; reason: string }>;
}

export function useImportClients() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (rows: ImportClientRow[]) => {
      // The backend caps at 2000 rows; chunk if larger.
      const BATCH = 200;
      const aggregate: ImportResult = {
        total: rows.length,
        created: 0,
        updated: 0,
        skipped: 0,
        errors: [],
      };
      for (let i = 0; i < rows.length; i += BATCH) {
        const batch = rows.slice(i, i + BATCH);
        const res = await apiFetch('/clients/import', {
          method: 'POST',
          body: JSON.stringify({ rows: batch }),
        });
        if (!res.ok) {
          const body = (await res.json().catch(() => ({}))) as { message?: string };
          throw new Error(body.message ?? `import_failed_${res.status}`);
        }
        const r = (await res.json()) as ImportResult;
        aggregate.created += r.created;
        aggregate.updated += r.updated;
        aggregate.skipped += r.skipped;
        for (const e of r.errors) {
          aggregate.errors.push({ ...e, index: e.index + i });
        }
      }
      return aggregate;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['clients'] }),
  });
}

export interface ClientSearchCriteria {
  id: string;
  client_id: string;
  min_price: number | null;
  max_price: number | null;
  min_size: number | null;
  max_size: number | null;
  rooms_min: number | null;
  districts: string[] | null;
  building_types: string[] | null;
  object_types: string[] | null;
  notes: string | null;
  created_at: number;
  updated_at: number;
}

export interface SearchCriteriaInput {
  minPrice?: number | null;
  maxPrice?: number | null;
  minSize?: number | null;
  maxSize?: number | null;
  roomsMin?: number | null;
  districts?: string[] | null;
  buildingTypes?: string[] | null;
  objectTypes?: string[] | null;
  notes?: string | null;
}

export function useSearchCriteria(clientId: string | undefined) {
  return useQuery({
    queryKey: ['client', clientId, 'search-criteria'] as const,
    queryFn: async () => {
      const res = await apiFetch(`/clients/${clientId}/search-criteria`);
      if (!res.ok) throw new Error(`sc_failed_${res.status}`);
      const data = (await res.json()) as ClientSearchCriteria | null | Record<string, never>;
      // API returns the row or undefined; normalize to null
      return data && 'id' in data ? data : null;
    },
    enabled: !!clientId,
  });
}

export function useUpsertSearchCriteria(clientId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: SearchCriteriaInput) => {
      const res = await apiFetch(`/clients/${clientId}/search-criteria`, {
        method: 'PUT',
        body: JSON.stringify(input),
      });
      if (!res.ok) {
        const j = (await res.json().catch(() => ({}))) as { message?: string };
        throw new Error(j.message ?? `sc_save_failed_${res.status}`);
      }
      return (await res.json()) as ClientSearchCriteria;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['client', clientId, 'search-criteria'] });
      qc.invalidateQueries({ queryKey: ['client', clientId, 'match-properties'] });
    },
  });
}

export interface PropertyMatchRow {
  propertyId: string;
  score: number;
  matched: string[];
  address: string | null;
  city: string;
  postalCode: string | null;
  district: string | null;
  purchasePrice: number | null;
  rooms: string | null;
  size: number | null;
  buildingType: string | null;
}

export function usePropertyMatches(clientId: string | undefined) {
  return useQuery({
    queryKey: ['client', clientId, 'match-properties'] as const,
    queryFn: async () => {
      const res = await apiFetch(`/clients/${clientId}/match-properties`);
      if (!res.ok) throw new Error(`match_failed_${res.status}`);
      const json = (await res.json()) as { items: PropertyMatchRow[] };
      return json.items;
    },
    enabled: !!clientId,
    staleTime: 60_000,
  });
}

export function useSendClientEmail() {
  return useMutation({
    mutationFn: async ({
      id,
      subject,
      body,
    }: {
      id: string;
      subject: string;
      body: string;
    }) => {
      const res = await apiFetch(`/clients/${id}/email`, {
        method: 'POST',
        body: JSON.stringify({ subject, body }),
      });
      if (!res.ok) {
        const j = (await res.json().catch(() => ({}))) as { message?: string; error?: string };
        throw new Error(j.error ?? j.message ?? `email_failed_${res.status}`);
      }
      return (await res.json()) as { ok: boolean; message_id?: string };
    },
  });
}

export function useAddPropertyInterest(clientId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ propertyId, notes }: { propertyId: string; notes?: string }) => {
      const res = await apiFetch(`/clients/${clientId}/property-interests`, {
        method: 'POST',
        body: JSON.stringify({ propertyId, notes: notes ?? undefined }),
      });
      if (!res.ok) {
        const j = (await res.json().catch(() => ({}))) as { message?: string };
        throw new Error(j.message ?? `interest_failed_${res.status}`);
      }
      return (await res.json()) as Record<string, unknown>;
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['client', clientId, 'property-interests'] }),
  });
}

export function useSendClientSms() {
  return useMutation({
    mutationFn: async ({
      id,
      message,
      type = 'sms',
    }: {
      id: string;
      message: string;
      type?: 'sms' | 'whatsapp';
    }) => {
      const res = await apiFetch(`/clients/${id}/sms`, {
        method: 'POST',
        body: JSON.stringify({ message, type }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { message?: string };
        throw new Error(body.message ?? 'sms_failed');
      }
      return (await res.json()) as { recordId: string; status: 'sent' | 'failed'; error?: string };
    },
  });
}
